import { useEffect, useLayoutEffect, useRef } from "react";

/**
 * Registers a primary action with the host modal footer.
 *
 * Child forms that used to render their own Save / Create buttons call this
 * instead; the host renders the button in its footer and invokes `onSubmit`.
 * No-op when `register` is not provided (component rendered outside the
 * creation modal), so legacy hosts keep their inline buttons.
 *
 * @param {((key: string, action: object|null) => void)|undefined} register
 * @param {string} key Registry slot (`"form"`, `"mapping"`); the host decides precedence.
 * @param {object} action
 * @param {string} action.label
 * @param {() => void} action.onSubmit
 * @param {boolean} [action.isLoading]
 * @param {boolean} [action.isDisabled]
 * @param {string} [action.iconSrc] Brand logo rendered before the label.
 * @param {boolean} [action.hidden] Clears the slot while true.
 */
const useHostFooterAction = (
    register,
    key,
    {
        label,
        onSubmit,
        isLoading = false,
        isDisabled = false,
        iconSrc,
        hidden = false,
    },
) => {
    const onSubmitRef = useRef(onSubmit);
    onSubmitRef.current = onSubmit;

    // Layout effect so the footer button appears in the same paint as the form.
    useLayoutEffect(() => {
        if (!register) return undefined;
        if (hidden) {
            register(key, null);
            return undefined;
        }
        register(key, {
            label,
            isLoading,
            isDisabled,
            iconSrc,
            onSubmit: () => onSubmitRef.current?.(),
        });
        return undefined;
    }, [register, key, label, isLoading, isDisabled, iconSrc, hidden]);

    useEffect(() => {
        if (!register) return undefined;
        return () => register(key, null);
    }, [register, key]);
};

export default useHostFooterAction;
